import { createContext, useContext, useEffect, useState, ReactNode } from "react"
import { useRouter, usePathname } from "@/lib/router-helpers"
import {jwtDecode} from "jwt-decode"; 
import Cookies from "js-cookie"; 

export interface User {
  id?: string
  mobile: string
  firstname: string
  lastname: string
  email?: string
  active?: boolean
  created_at?: Date
  updated_at?: Date

}
//Testtt
interface JwtCustomPayload {
  data: User
  exp: number
  iat: number
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  login: ( token: string) => void
  logout: () => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children}: { children: ReactNode}) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()
  const pathname = usePathname()

  useEffect(() => {
    // Load user and token from localStorage on mount
    if (typeof window === 'undefined') {
      setIsLoading(false)
      return
    }
    
    try {
      const storedUser = localStorage.getItem("qetero.user")
      const storedToken = Cookies.get("qetero.token")

      console.log('storedUser', storedUser)
      console.log('storedToken', storedToken)
      
      if (storedToken) {
        try {
          const decoded = jwtDecode<JwtCustomPayload>(storedToken)
          const currentTime = Date.now() / 1000

          console.log('decoded token', decoded)
          console.log('current time', currentTime) 
          console.log('token expiration time', decoded.exp)
          console.log('is token expired?', decoded.exp < currentTime)
          
          if (decoded.exp < currentTime) {
            console.log("Token expired")
            localStorage.removeItem("qetero.user")
            Cookies.remove("qetero.token")
            localStorage.removeItem("qetero.currentUserPhone")
            setUser(null)
            setToken(null)
          } else {
            setToken(storedToken)
            if (storedUser) {
              setUser(JSON.parse(storedUser))
            }
          }
        } catch (error) {
          console.error("Invalid token:", error)
          localStorage.removeItem("qetero.user")
          Cookies.remove("qetero.token")
          localStorage.removeItem("qetero.currentUserPhone")
          setUser(null)
          setToken(null)
        }
      }
    } catch {
      setUser(null)
      setToken(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Protect routes that require authentication
  useEffect(() => {
    if (isLoading) return

    const protectedRoutes = ["/booking/service", "/booking/schedule", "/booking/history"]
    const isProtectedRoute = protectedRoutes.some((route) => pathname?.includes(route) || pathname?.includes(route + "/"))
    
    // Check both user and token to ensure proper authentication
    // Also check cookie directly to handle race conditions during login/navigation
    const cookieToken = Cookies.get("qetero.token")
    
    let isTokenExpired = false
    if (cookieToken) {
      try {
        const decoded = jwtDecode<JwtCustomPayload>(cookieToken)
        if (decoded.exp < Date.now() / 1000) {
          isTokenExpired = true
        }
      } catch {
        isTokenExpired = true
      }
    }

    if (isProtectedRoute && ((!token && !cookieToken) || isTokenExpired)) {
      console.log('Auth Guard: Redirecting to verify. Path:', pathname)
      
      if (isTokenExpired) {
        localStorage.removeItem("qetero.user")
        Cookies.remove("qetero.token")
        localStorage.removeItem("qetero.currentUserPhone")
        setUser(null)
        setToken(null)
      }

      if (typeof window !== 'undefined') {
        const returnUrl = encodeURIComponent(pathname + window.location.search)
        window.location.href = `/auth/verify?returnUrl=${returnUrl}`
      }
    }
  }, [user, token, isLoading, pathname, router])

  const login = (tokenValue: string) => {
    if (typeof window === 'undefined') return
    
    const decodedToken = jwtDecode<JwtCustomPayload>(tokenValue);;
    const userData : User = decodedToken?.data    
   localStorage.setItem("qetero.user", JSON.stringify(userData))
    Cookies.set("qetero.token", tokenValue, { expires: 7, path: '/' }) // Expires in 7 days
   localStorage.setItem("qetero.currentUserPhone", userData.mobile)
   setUser(userData)
    setToken(tokenValue)
  }

  const logout = () => {
    if (typeof window === 'undefined') return
    
    localStorage.removeItem("qetero.user")
    Cookies.remove("qetero.token")
    localStorage.removeItem("qetero.currentUserPhone")
    setUser(null)
    setToken(null)
    
    // Force a full page reload with cache-busting for Safari
    // Safari aggressively caches RSC payloads, causing raw JSON to display
    const timestamp = Date.now()
    const targetUrl = `/auth/verify/?_=${timestamp}`
    
    // Use location.replace to avoid back-button issues and force fresh load
    window.location.replace(targetUrl)
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, isAuthenticated: !!token}}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}